import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  updateChatListOrder,
  updateGroupChatLastMessage,
  clearUnread,
  setTypingUser,
  clearPendingForChat
} from "../slices/chat/reducer";
import { socket } from "../common/socket";

const ChatSocketBridge = () => {
  const dispatch = useDispatch();
  const activeChatId = useSelector((state) => state.Chat?.activeChatId);
  const activeChatIdRef = useRef(activeChatId);

  useEffect(() => {
    activeChatIdRef.current = activeChatId;
  }, [activeChatId]);

  useEffect(() => {
    // In ChatSocketBridge.js - MODIFY the handleChatListUpdate function
    const handleChatListUpdate = (event) => {
      const data = event.detail || {};
      console.log("🔄 [BRIDGE] Chat list update event:", {
        hasData: !!data,
        chatId: data.chatId,
        action: data.action,
        timestamp: new Date().toISOString()
      });

      if (!data) return;
      const chatId = data.chatId || data.lastMessage?.chatRequestId;
      if (!chatId) return;

      console.log("📋 [BRIDGE] Processing chat list update for:", chatId);

      dispatch(updateChatListOrder(chatId));

      if (data.lastMessage) {
        dispatch(
          updateGroupChatLastMessage({
            chatId,
            lastMessage: data.lastMessage,
            lastMessageTimestamp: data.lastMessage.createdAt || new Date().toISOString(),
          })
        );
      }

      const isActive = activeChatIdRef.current && String(activeChatIdRef.current) === String(chatId);

      // ✅ FIXED: Only update unread counts for inactive chats, don't increment
      if (!isActive) {
        console.log("📈 [BRIDGE] Marking chat as having unread (no increment):", chatId);
        // Don't call incrementUnread - let API handle the count
      } else {
        console.log("✅ [BRIDGE] Active chat, no unread needed:", chatId);
        dispatch(clearUnread(chatId));
      }
    };

    const handleGlobalNewMessage = (event) => {
      const message = event.detail;
      console.log("💬 [BRIDGE] Global new message event:", {
        messageId: message?._id,
        chatId: message?.chatId,
        hasContent: !!(message?.content || message?.mediaUrl),
        timestamp: new Date().toISOString()
      });

      if (!message) return;
      const chatId = message.chatId || message.chatRequestId;
      if (!chatId) return;

      console.log("🔄 [BRIDGE] Processing global message for chat:", chatId);

      const isActive = activeChatIdRef.current && String(activeChatIdRef.current) === String(chatId);
      console.log("🎯 [BRIDGE] Global message active status:", {
        chatId: chatId,
        activeChatId: activeChatIdRef.current,
        isActive: isActive
      });


      console.log("✅ [BRIDGE] Clearing pending for active chat:", chatId);
      try {
        const authUser = JSON.parse(localStorage.getItem("authUser"));
        const myId = authUser?.user?._id || authUser?._id;
        if (myId) {
          dispatch(clearPendingForChat({ chatId, userId: myId }));
        }
      } catch (error) {
        console.error("Error clearing pending count:", error);
      }
    };

    console.log("🔔 [BRIDGE] Setting up window event listeners");
    window.addEventListener("socket:chatListUpdate", handleChatListUpdate);
    window.addEventListener("socket:newMessage", handleGlobalNewMessage);

    return () => {
      console.log("🧹 [BRIDGE] Removing window event listeners");
      window.removeEventListener("socket:chatListUpdate", handleChatListUpdate);
      window.removeEventListener("socket:newMessage", handleGlobalNewMessage);
    };
  }, [dispatch]);

  useEffect(() => {
    const handleTypingSocket = (data = {}) => {
      console.log("⌨️ [BRIDGE] Received userTyping event:", {
        chatId: data.chatId,
        userId: data.userId,
        userEmail: data.user?.email,
        isTyping: data.isTyping,
        socketId: socket.id,
        timestamp: new Date().toISOString()
      });

      if (!data.chatId || !data.userId) {
        console.error("❌ [BRIDGE] Invalid typing data:", data);
        return;
      }

      console.log("🎯 [BRIDGE] Dispatching typing user:", {
        chatId: data.chatId,
        userId: data.userId,
        isTyping: data.isTyping
      });

      dispatch(
        setTypingUser({
          chatId: data.chatId,
          userId: data.userId,
          isTyping: data.isTyping,
        })
      );
    };

    console.log("🔔 [BRIDGE] Setting up socket typing listener");
    socket.on("userTyping", handleTypingSocket);

    return () => {
      console.log("🧹 [BRIDGE] Removing socket typing listener");
      socket.off("userTyping", handleTypingSocket);
    };
  }, [dispatch]);

  return null;
};

export default ChatSocketBridge;